import { REQUIRED_FIELDS_BY_KIND } from "../../redux/modules/invoices/invoiceFields";

export const validateIsEmail = ({ value, translate }) => {
  if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return translate("Your email seems to be invalid");
  }
};

export const validateRequireAllFields = ({ formData, translate }) => {
  const newErrors = {};

  Object.keys(formData).forEach(key => {
    if (!formData[key].trim()) {
      newErrors[key] = translate("This field is required");
    }
  });

  return newErrors;
};

/**
 * Validates only the fields required for a given business activity kind
 * (web and native share this logic via REQUIRED_FIELDS_BY_KIND).
 *
 * @param {string} kind - BUSINESS_ACTIVITY_KIND value
 * @param {Object} formData - Raw form data (may have undefined values)
 * @param {Function} translate
 * @returns {Object} Errors keyed by field name
 */
export const validateInvoiceFormForKind = (kind, formData, translate) => {
  const requiredFields = REQUIRED_FIELDS_BY_KIND[kind] ?? [];
  const safeFormData = Object.fromEntries(
    requiredFields.map(field => [field, formData[field] ?? ""])
  );
  return validateRequireAllFields({ formData: safeFormData, translate });
};

export const isFieldRequiredForKind = (kind, field) => {
  return (REQUIRED_FIELDS_BY_KIND[kind] ?? []).includes(field);
};

/**
 * Shared submit handler for the three invoice forms
 * (private_person, other_business, self_employed; web and native).
 * Runs validate(), applies errors to form state, returns formData or false.
 *
 * @param {Object} params
 * @param {Function} params.validate - Sync or async; returns errors object
 * @param {Object} params.formData - Current form data to return on success
 * @param {Function} params.updateInvoiceFormError
 * @param {string} params.formDataKey - "privatePersonData" | "otherBusinessData" | "selfEmployedData"
 * @returns {Promise<Object|false>} formData on success, false when validation fails
 */
export const submitInvoiceForm = async ({
  validate,
  formData,
  updateInvoiceFormError,
  formDataKey
}) => {
  const validationErrors = await validate();
  updateInvoiceFormError({ [formDataKey]: validationErrors });

  const hasErrors = Object.values(validationErrors).some(Boolean);
  if (hasErrors) {
    return false;
  }

  return formData;
};

/**
 * Shared change handler factory for the three invoice forms (web and native).
 * Returns a curried handler: (field) => (value) => void that updates form data and clears the field error.
 *
 * @param {Object} params
 * @param {Object} params.formData - Current form data
 * @param {Object} params.errors - Current field errors
 * @param {Function} params.updateInvoiceFormData
 * @param {Function} params.updateInvoiceFormError
 * @param {string} params.formDataKey - "privatePersonData" | "otherBusinessData" | "selfEmployedData"
 * @returns {(field: string) => (value: string) => void}
 */
export const createHandleChange = ({
  formData,
  errors,
  updateInvoiceFormData,
  updateInvoiceFormError,
  formDataKey
}) => field => value => {
  updateInvoiceFormData({
    [formDataKey]: {
      ...formData,
      [field]: value
    }
  });

  updateInvoiceFormError({
    [formDataKey]: {
      ...errors,
      [field]: ""
    }
  });
};
